import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Typography } from '@/components/ui/atoms/Typography';
import { Tag } from '@/components/ui/atoms/Tag';
import { Avatar } from '@/components/ui/atoms/Avatar';
import { SkillChip } from '@/components/ui/atoms/SkillChip';
import { Button } from '@/components/ui/atoms/Button';
import {
  columnFilterHeader,
  resolveColumnFilterFn,
  type ColumnFilterSpec,
  type ColumnFilterHeaderLabels,
} from '@/components/ui/molecules/ColumnFilter';
import { formatYearsOfExperience } from '@/hooks/useEnhancedData';
import type { TalentPoolListItem, CandidateStatus } from '@/types/talent.types';

// ── Row model ─────────────────────────────────────────────────────────────

export interface TalentPoolTableRow {
  id: string;
  fullName: string;
  email: string;
  avatarUrl?: string;
  /** Initials derived from fullName for Avatar fallback. */
  initials: string;
  /** First 3 skills (display labels). */
  topSkills: string[];
  /** Total skills count (including those beyond the first 3). */
  totalSkillsCount: number;
  /** All skill names joined for global quick-search. */
  skillsText: string;
  /** All skill names (array) — used by the multiSelect Skills column filter. */
  allSkills: string[];
  totalYoe: number;
  jobsCount: number;
  latestStatus?: CandidateStatus;
  /** Status the talent holds on each job they are a candidate for (one per job). */
  jobStatuses: CandidateStatus[];
  /**
   * Tenant-defined grouping values for this talent (NOT a fixed platform concept).
   * Different tenants organize talent differently (team / department / business unit /
   * tag); this column is generic + data-driven and only rendered when data has values.
   */
  groups: string[];
}

/**
 * Derive 2-letter initials from a full name (first + last word initials).
 */
function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (
    parts[0].charAt(0).toUpperCase() +
    parts[parts.length - 1].charAt(0).toUpperCase()
  );
}

/**
 * Map a TalentPoolListItem to a flat TalentPoolTableRow for the DataTable.
 * `skillsText` concatenates all skill names (technical + soft) for global quick-search;
 * `allSkills` keeps them as an array for the Skills multiSelect column filter.
 */
export function mapTalentToRow(item: TalentPoolListItem): TalentPoolTableRow {
  const allSkillNames = item.allSkills.map((s) => s.name);
  return {
    id: item.id,
    fullName: item.fullName,
    email: item.email,
    avatarUrl: item.avatarUrl,
    initials: getInitials(item.fullName),
    topSkills: item.topSkills.slice(0, 3).map((s) => s.name),
    totalSkillsCount: item.allSkills.length,
    skillsText: allSkillNames.join(' '),
    allSkills: allSkillNames,
    totalYoe: item.totalYoe,
    jobsCount: item.jobsCount,
    // Normalize status casing: the API may send lowercase ("pending"); the
    // status column set, i18n keys, and StatusPill all key off the UPPERCASE
    // CandidateStatus union, so uppercase here for robustness to either casing.
    latestStatus: item.latestStatus
      ? (String(item.latestStatus).toUpperCase() as CandidateStatus)
      : item.latestStatus,
    jobStatuses: (item.jobStatuses ?? []).map(
      (s) => String(s).toUpperCase() as CandidateStatus,
    ),
    // `businessUnits` is the current (soft, pre-backend) field name for the generic
    // tenant-defined grouping surfaced as the data-driven "groups" column.
    groups: item.businessUnits ?? [],
  };
}


export interface TalentPoolColumnFilterData {
  /** Distinct skill names across the pool (multiSelect options). */
  skillOptions: string[];
  /** Distinct tenant-defined grouping values (multiSelect options); empty hides the column. */
  groupOptions: string[];
  /** Upper bound for the Experience range slider. */
  maxYoe: number;
  /** Upper bound for the Jobs count range slider. */
  maxJobs: number;
}

export interface CreateTalentPoolTableColumnsOptions {
  /** t function from useTranslation('talentPool') */
  t: ReturnType<typeof useTranslation<'talentPool'>>['t'];
  /** Derived filter option/range data from the full dataset. */
  filterData: TalentPoolColumnFilterData;
  /** When provided, renders a per-row delete (trash) action column. */
  onDeleteTalent?: (row: TalentPoolTableRow) => void;
}

/**
 * Build the per-column filter spec map plus shared popover labels. Returned so the
 * page can feed the same specs to AppliedFiltersBar (single source of truth).
 */
export function buildTalentPoolFilterSpecs(
  t: CreateTalentPoolTableColumnsOptions['t'],
  data: TalentPoolColumnFilterData,
): { specs: Record<string, ColumnFilterSpec>; labels: ColumnFilterHeaderLabels } {
  const labels: ColumnFilterHeaderLabels = {
    searchPlaceholder: t('pool.filter.searchPlaceholder'),
    clear: t('pool.filter.clear'),
    noOptions: t('pool.filter.noOptions'),
    filterAria: t('pool.filter.filterAria'),
  };

  const specs: Record<string, ColumnFilterSpec> = {
    fullName: {
      type: 'text',
      label: t('pool.col.name'),
    },
    skillsText: {
      type: 'multiSelect',
      label: t('pool.col.skills'),
      matchMode: 'all',
      options: data.skillOptions.map((s) => ({ value: s, label: s })),
    },
    totalYoe: {
      type: 'range',
      label: t('pool.col.yoe'),
      min: 0,
      max: data.maxYoe,
      unit: t('pool.yoeSuffix'),
    },
    jobsCount: {
      type: 'range',
      label: t('pool.col.jobs'),
      min: 0,
      max: data.maxJobs,
    },
  };

  // Generic, tenant-defined grouping — only surfaced when the data actually has values.
  if (data.groupOptions.length > 0) {
    specs.groups = {
      type: 'multiSelect',
      label: t('pool.col.group'),
      matchMode: 'any',
      options: data.groupOptions.map((g) => ({ value: g, label: g })),
    };
  }

  return { specs, labels };
}

/**
 * Factory returning the column defs for the talent pool DataTable. Every visible column
 * is sortable and filterable: each attaches a `ColumnFilterSpec` to `meta.filter`, a
 * matching `filterFn`, and the `columnFilterHeader` (sort toggle + funnel popover).
 */
export function createTalentPoolTableColumns({
  t,
  filterData,
  onDeleteTalent,
}: CreateTalentPoolTableColumnsOptions): ColumnDef<TalentPoolTableRow>[] {
  const { specs, labels } = buildTalentPoolFilterSpecs(t, filterData);

  const columns: ColumnDef<TalentPoolTableRow>[] = [
    // ── 1. Name (text filter on full name) ─────────────────────────────────
    {
      accessorKey: 'fullName',
      meta: { label: t('pool.col.name'), filter: specs.fullName },
      filterFn: resolveColumnFilterFn(specs.fullName),
      header: columnFilterHeader(specs.fullName, labels),
      size: 240,
      minSize: 200,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar
            src={row.original.avatarUrl}
            initials={row.original.initials}
            alt={row.original.fullName}
            size="sm"
            variant="neutral"
          />
          <div className="min-w-0">
            <Typography variant="body-sm" weight="semibold" color="primary">
              {row.original.fullName}
            </Typography>
            <Typography variant="body-sm" weight="medium" color="muted" className="truncate">
              {row.original.email}
            </Typography>
          </div>
        </div>
      ),
    },

    // ── 2. Skills (multiSelect, matchMode 'all') ───────────────────────────
    {
      id: 'skillsText',
      accessorFn: (row) => row.allSkills,
      meta: { label: t('pool.col.skills'), filter: specs.skillsText },
      filterFn: resolveColumnFilterFn(specs.skillsText),
      header: columnFilterHeader(specs.skillsText, labels),
      enableSorting: false,
      size: 240,
      minSize: 200,
      cell: ({ row }) => {
        const { topSkills, totalSkillsCount } = row.original;
        const overflow = totalSkillsCount - topSkills.length;
        return (
          <div className="flex flex-wrap items-center gap-1">
            {topSkills.map((skill) => (
              <SkillChip key={skill} label={skill} tone="info" />
            ))}
            {overflow > 0 && (
              <Tag intent="secondary" variant="subtle">
                +{overflow}
              </Tag>
            )}
          </div>
        );
      },
    },

    // ── 3. Experience (range, unit = yr) ───────────────────────────────────
    {
      accessorKey: 'totalYoe',
      meta: { label: t('pool.col.yoe'), filter: specs.totalYoe },
      filterFn: resolveColumnFilterFn(specs.totalYoe),
      header: columnFilterHeader(specs.totalYoe, labels),
      size: 120,
      minSize: 96,
      cell: ({ row }) => {
        const label = formatYearsOfExperience(row.original.totalYoe) || '—';
        return (
          <Typography
            variant="body-sm"
            weight="medium"
            color="secondary"
            className="block truncate"
            title={label}
          >
            {label}
          </Typography>
        );
      },
    },

    // ── 4. Jobs (range, count) ─────────────────────────────────────────────
    {
      accessorKey: 'jobsCount',
      meta: { label: t('pool.col.jobs'), filter: specs.jobsCount },
      filterFn: resolveColumnFilterFn(specs.jobsCount),
      header: columnFilterHeader(specs.jobsCount, labels),
      size: 110,
      minSize: 90,
      cell: ({ row }) => (
        <Tag intent="secondary" variant="subtle">
          {row.original.jobsCount}
        </Tag>
      ),
    },

  ];

  // ── 6. Group (generic tenant-defined grouping) — only when data has values ──
  if (specs.groups) {
    columns.push({
      id: 'groups',
      accessorFn: (row) => row.groups,
      meta: { label: t('pool.col.group'), filter: specs.groups },
      filterFn: resolveColumnFilterFn(specs.groups),
      header: columnFilterHeader(specs.groups, labels),
      enableSorting: false,
      size: 160,
      minSize: 120,
      cell: ({ row }) => {
        const groups = row.original.groups;
        if (!groups.length) return null;
        return (
          <div className="flex flex-wrap items-center gap-1">
            {groups.map((g) => (
              <Tag key={g} intent="secondary" variant="subtle">
                {g}
              </Tag>
            ))}
          </div>
        );
      },
    });
  }

  // ── 7. Row actions (delete) — only when a handler is provided ───────────────
  if (onDeleteTalent) {
    columns.push({
      id: 'actions',
      header: '',
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      size: 56,
      minSize: 56,
      cell: ({ row }) => (
        <div className="flex justify-end pr-1">
          <Button
            type="icon"
            intent="muted"
            size="sm"
            icon={<Trash2/>}
            label={t('detail.deleteTalent')}
            title={t('detail.deleteTalent')}
            aria-label={t('detail.deleteTalent')}
            onClick={(e) => {
              // Don't trigger the row-click navigation.
              e.stopPropagation();
              onDeleteTalent(row.original);
            }}
          />
        </div>
      ),
    });
  }

  return columns;
}
